import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ClipboardList, ChevronRight, ChevronLeft, Check, AlertCircle, History } from "lucide-react";
import { API } from "../App";

const LIKERT_LABELS = [
  { value: 1, label: "Presque jamais", short: "1" },
  { value: 2, label: "Parfois", short: "2" },
  { value: 3, label: "Environ la moitie du temps", short: "3" },
  { value: 4, label: "La plupart du temps", short: "4" },
  { value: 5, label: "Presque toujours", short: "5" },
];

const ITEMS_PER_PAGE = 6;

export default function DersQuestionnairePage({ user, logout }) {
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [responses, setResponses] = useState({});
  const [page, setPage] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pendingRes, itemsRes, histRes] = await Promise.all([
          axios.get(`${API}/ders/pending`, { withCredentials: true }),
          axios.get(`${API}/ders/items`, { withCredentials: true }),
          axios.get(`${API}/ders/history`, { withCredentials: true }),
        ]);
        setPending(pendingRes.data);
        setItems(itemsRes.data.items);
        setHistory(histRes.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);
  const currentItems = items.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);
  const answeredCount = Object.keys(responses).length;
  const allAnswered = answeredCount === 36;

  const startQuestionnaire = (assignment) => {
    setActiveAssignment(assignment);
    setResponses({});
    setPage(0);
  };

  const handleResponse = (itemId, value) => {
    setResponses((prev) => ({ ...prev, [String(itemId)]: value }));
  };

  const handleSubmit = async () => {
    if (!allAnswered || !activeAssignment) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/ders/submit`, {
        assignment_id: activeAssignment.assignment_id,
        responses,
      }, { withCredentials: true });
      toast.success("Questionnaire DERS soumis avec succes !");
      setActiveAssignment(null);
      setResponses({});
      setPage(0);
      // Refresh
      const [pendingRes, histRes] = await Promise.all([
        axios.get(`${API}/ders/pending`, { withCredentials: true }),
        axios.get(`${API}/ders/history`, { withCredentials: true }),
      ]);
      setPending(pendingRes.data);
      setHistory(histRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la soumission");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  // Active questionnaire view
  if (activeAssignment) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="max-w-3xl mx-auto space-y-6 pb-12" data-testid="ders-questionnaire-active">
          {/* Header */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Button variant="ghost" size="sm" onClick={() => setActiveAssignment(null)} data-testid="ders-back-btn">
                <ChevronLeft className="h-4 w-4 mr-1" /> Retour
              </Button>
            </div>
            <h1 className="text-2xl font-bold text-foreground">Questionnaire DERS</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Phase : <Badge variant="secondary">{activeAssignment.phase === "pre" ? "Pre-psychoeducation" : "Post-psychoeducation"}</Badge>
            </p>
          </div>

          {/* Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{answeredCount}/36 reponses</span>
              <span>Page {page + 1}/{totalPages}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${(answeredCount / 36) * 100}%` }}
                data-testid="ders-progress-bar"
              />
            </div>
          </div>

          {/* Instructions */}
          <Card className="bg-blue-50/50 dark:bg-blue-950/20 border-blue-200/50 dark:border-blue-800/30">
            <CardContent className="p-4 text-sm text-blue-800 dark:text-blue-200">
              <AlertCircle className="h-4 w-4 inline mr-2" />
              Indiquez a quelle frequence les affirmations suivantes s'appliquent a vous, en utilisant l'echelle ci-dessous :
              <span className="font-medium"> 1 = Presque jamais, 5 = Presque toujours</span>
            </CardContent>
          </Card>

          {/* Questions */}
          <div className="space-y-4">
            {currentItems.map((item) => (
              <Card
                key={item.id}
                className={`transition-all duration-200 ${responses[String(item.id)] ? "border-primary/30 bg-primary/5" : "border-border/40"}`}
                data-testid={`ders-item-${item.id}`}
              >
                <CardContent className="p-4">
                  <p className="text-sm font-medium text-foreground mb-3">
                    <span className="text-muted-foreground mr-2">{item.id}.</span>
                    {item.text}
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {LIKERT_LABELS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => handleResponse(item.id, opt.value)}
                        className={`
                          flex-1 min-w-[60px] py-2.5 px-2 rounded-lg text-xs font-medium transition-all duration-200 border
                          ${responses[String(item.id)] === opt.value
                            ? "bg-primary text-white border-primary shadow-sm"
                            : "bg-card text-muted-foreground border-border/50 hover:border-primary/40 hover:bg-primary/5"
                          }
                        `}
                        data-testid={`ders-item-${item.id}-opt-${opt.value}`}
                      >
                        <div className="text-center">
                          <div className="font-bold text-sm">{opt.short}</div>
                          <div className="hidden sm:block mt-0.5 leading-tight">{opt.label}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center pt-4">
            <Button
              variant="outline"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              data-testid="ders-prev-page"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Precedent
            </Button>

            {page < totalPages - 1 ? (
              <Button
                onClick={() => setPage((p) => p + 1)}
                data-testid="ders-next-page"
              >
                Suivant <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!allAnswered || submitting}
                className="bg-green-600 hover:bg-green-700"
                data-testid="ders-submit-btn"
              >
                {submitting ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-1" />
                )}
                Soumettre ({answeredCount}/36)
              </Button>
            )}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // List view - pending assignments + history
  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="max-w-3xl mx-auto space-y-6 pb-12" data-testid="ders-page">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Questionnaire DERS</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Echelle des Difficultes de Regulation Emotionnelle
          </p>
        </div>

        {/* Pending assignments */}
        {pending.length > 0 ? (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-foreground">Questionnaires en attente</h2>
            {pending.map((a) => (
              <Card key={a.assignment_id} className="border-amber-200/60 dark:border-amber-800/40 bg-amber-50/30 dark:bg-amber-950/10" data-testid={`ders-pending-${a.assignment_id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                      <ClipboardList className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        DERS — {a.phase === "pre" ? "Pre-psychoeducation" : "Post-psychoeducation"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Assigne le {new Date(a.created_at).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => startQuestionnaire(a)} data-testid={`ders-start-${a.assignment_id}`}>
                    Commencer <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-border/40">
            <CardContent className="p-8 text-center">
              <ClipboardList className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                Aucun questionnaire DERS en attente.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Votre therapeute vous assignera un questionnaire lorsqu'il sera necessaire.
              </p>
            </CardContent>
          </Card>
        )}

        {/* History toggle */}
        {history.length > 0 && (
          <div>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
              data-testid="ders-history-toggle"
            >
              <History className="h-4 w-4" />
              Historique ({history.length})
              {showHistory ? <ChevronLeft className="h-3.5 w-3.5 rotate-90" /> : <ChevronRight className="h-3.5 w-3.5 rotate-90" />}
            </button>

            {showHistory && (
              <div className="mt-3 space-y-2">
                {history.map((entry) => (
                  <Card key={entry.entry_id} className="border-border/30" data-testid={`ders-history-${entry.entry_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">
                            Phase : <Badge variant="secondary" className="ml-1">
                              {entry.phase === "pre" ? "Pre" : "Post"}
                            </Badge>
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(entry.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-foreground">{entry.scoring?.total}</p>
                          <p className="text-xs text-muted-foreground">/180 ({entry.scoring?.total_percent}%)</p>
                        </div>
                      </div>
                      {/* Subscale bars */}
                      <div className="mt-3 space-y-1.5">
                        {entry.scoring?.subscales && Object.entries(entry.scoring.subscales).map(([key, sub]) => (
                          <div key={key} className="flex items-center gap-2">
                            <span className="text-[10px] text-muted-foreground w-[130px] truncate">{sub.label}</span>
                            <div className="flex-1 bg-muted rounded-full h-1.5">
                              <div
                                className="bg-primary h-1.5 rounded-full transition-all"
                                style={{ width: `${sub.percent}%` }}
                              />
                            </div>
                            <span className="text-[10px] font-medium w-[40px] text-right">{sub.percent}%</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
